/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { AbstractCheckboxListElement } from './abstract-checkbox-list-element.component';
import { ChecklistDataMap, ChecklistItemId, IChecklistItemDataWithId } from './checkbox-list-model';

export enum SideEnum {
	LEFT = 'left',
	RIGHT = 'right',
}

// Note:  this is the same as:   type Side = 'left' | 'right';
// it's easier to reference these Side string values in HTML than enum
// constants, which require the enum name prefix, e.g. Side.RIGHT:
export type Side = `${SideEnum}`;

@Injectable()
export class CheckboxListModelService {
	private modelUpdatedSubject = new Subject<{ emitEvent: boolean }>();
	public modelUpdated$ = this.modelUpdatedSubject.asObservable();
	public modelUpdatedForExternalUse$ = this.modelUpdated$.pipe(filter((config) => config.emitEvent));

	private _currentMapOfValues: ChecklistDataMap = new Map();

	private _iconCount$ = new BehaviorSubject<number>(0);
	public iconCount$ = this._iconCount$.asObservable();

	private _checkboxSide$ = new BehaviorSubject<Side>(SideEnum.RIGHT);
	public checkboxSide$ = this._checkboxSide$.asObservable();

	private disabledSubject = new BehaviorSubject<boolean>(false);
	public disabled$ = this.disabledSubject.asObservable();

	private elementsWithIcons = new Set<AbstractCheckboxListElement>();

	/*
		A utility which can be used to convert a CheckboxListModelService's Map-based model
		(i.e. "_currentMapOfValues") to an array, where the array includes the Map's ID/keys
		and the Map's values.

		Such an array MAY be useful when writing an *ngFor of a <checkbox-list-item>,
		since that *ngFor will want to provide an ORDERED list of model objects.
		The Map is not ordered.
		While the array returned by this function isn't sorted, there are other standard
		array utilities for sorting.

		Use of this utility isn't required.  The ordered model data used in the *ngFor may
		be prepared in some other way.
	*/
	public static convertMapToArray(mapModel: ChecklistDataMap): IChecklistItemDataWithId[] {
		const resultArray = [] as IChecklistItemDataWithId[];
		for (const [key, value] of mapModel) {
			const valueCopy = {} as IChecklistItemDataWithId;
			valueCopy.id = key;
			valueCopy.originalModel = value.originalModel;
			resultArray.push(valueCopy);
		}
		return resultArray;
	}

	public get checkboxSide(): Side {
		return this._checkboxSide$.value;
	}

	public set checkboxSide(side: Side) {
		this._checkboxSide$.next(side);
	}

	public get currentMapOfValues(): ChecklistDataMap {
		return this._currentMapOfValues;
	}

	/**
	 * This will _always_ be considered a system update as it's the only current use
	 * and setter can't have config. Will need to convert to a set method if config needed
	 */
	public set currentMapOfValues(checklistData: ChecklistDataMap) {
		this._currentMapOfValues = checklistData;
		this.modelUpdatedSubject.next({ emitEvent: false });
	}

	public get currentArrayOfValues(): IChecklistItemDataWithId[] {
		return CheckboxListModelService.convertMapToArray(this._currentMapOfValues);
	}

	public setElementHasAnIcon(element: AbstractCheckboxListElement, hasAnIcon: boolean) {
		if (hasAnIcon) {
			this.elementsWithIcons.add(element);
		} else {
			this.elementsWithIcons.delete(element);
		}
		this._iconCount$.next(this.elementsWithIcons.size);
	}

	public includesId(id: string) {
		return this._currentMapOfValues.has(id);
	}

	public allIds() {
		return this._currentMapOfValues ? Array.from(this._currentMapOfValues.keys()) : [];
	}

	public allIsCheckedValuesEqual(bool: boolean, checkboxIds?: ChecklistItemId[]) {
		const ids = checkboxIds || this.allIds();
		for (const id of ids) {
			const checkboxData = this._currentMapOfValues.get(id);
			if (checkboxData?.isChecked !== bool) {
				return false;
			}
		}
		return true;
	}

	public getItemValue(checkboxId: ChecklistItemId) {
		const checkboxData = this._currentMapOfValues.get(checkboxId);
		return checkboxData ? checkboxData.isChecked : false;
	}

	public setItemValue(
		checkboxId: ChecklistItemId,
		newValue: boolean,
		updateObservable = true,
		emitExternally = true
	) {
		const checkboxData = this._currentMapOfValues.get(checkboxId);
		if (checkboxData) {
			checkboxData.isChecked = newValue;
			if (updateObservable) {
				this.modelUpdatedSubject.next({ emitEvent: emitExternally });
			}
		}
	}

	public updateSetsItems(checkboxIds?: ChecklistItemId[], emitExternally = true) {
		const idsToUse = checkboxIds ?? this.allIds();

		if (this.allIsCheckedValuesEqual(false, idsToUse)) {
			// All in the set are false, all need to be toggled to true.
			for (const id of idsToUse) {
				this.setItemValue(id, true, false);
			}
		} else {
			// One or more, or all, are true: ones that are true need to be toggled to false.
			for (const id of idsToUse) {
				if (this.getItemValue(id)) {
					this.setItemValue(id, false, false);
				}
			}
		}
		this.modelUpdatedSubject.next({ emitEvent: emitExternally });
	}

	public getItemIsCheckedChange$(checkboxId: ChecklistItemId) {
		return this.modelUpdated$.pipe(
			map(() => this._currentMapOfValues.get(checkboxId)?.isChecked),
			distinctUntilChanged()
		);
	}

	//currently exclusively called by the forms api (and thus always a system change)
	//this assumption will need to change if called... "manually"
	public setDisabledState(isDisabled: boolean): void {
		if (this.disabledSubject.value !== isDisabled) {
			this.disabledSubject.next(isDisabled);
		}
	}
}
